<template>
  <div style="position: relative; width: 100%; height: 100%">
    <video style="position: relative; width: 100%; height: 100%" ref="videoElement" class="video-js" controls autoplay playsinline></video>
  </div>
</template>

<script setup>
import { onMounted, onUnmounted, ref, defineProps, defineExpose, watch, defineEmits, computed, toRaw } from 'vue';
import videojs from 'video.js';
import 'video.js/dist/video-js.css';
import { PlayerState } from './constants/videoPlayer.js';
const emit = defineEmits([
  'player-error',
  'player-start',
  'player-ended',
  'player-unexpectedly-ended',
  'token-refresh',
  'video-seeked',
  'video-seeking',
  'video-paused',
  'video-played',
]);
import { debounce } from 'lodash';
import 'videojs-contrib-quality-levels';
import hlsQualitySelector from 'videojs-hls-quality-selector';

// 在組件定義外直接註冊
if (!videojs.getPlugin('hlsQualitySelector')) {
  videojs.registerPlugin('hlsQualitySelector', hlsQualitySelector);
}
// 備份原始 XMLHttpRequest.open 方法 用於組件卸載時還原全域設定
let originalXMLHttpRequestOpen = null;

const props = defineProps({
  streamingType: {
    type: String,
    required: true,
  },
  src: {
    type: String,
    required: true,
  },
  // Control the timing for changing the video source.
  // Set to false if the video source does not need to be changed.
  // Set to true when a source change is required, and revert to false after the video starts playing normally
  // to prevent the video from flickering because video.js restarts playback when the src is loaded.
  forceReloadWhenSrcChange: {
    type: Boolean,
    default: false,
  },
  width: {
    type: String,
    default: '100%',
  },
});

const videoElement = ref(null);
const isVideoWait = ref(false);
const isVideoSeeking = ref(false);
const isQualitySwitching = ref(false);

const player = ref(null);

const playerState = ref(PlayerState.Created);

const IvsPlayerInstance = computed(() => {
  return player.value?.getIVSPlayer();
});

// Workaround
// when timeupdate, call this function, if not called after 10 seconds, check if the video is playing
// if state is playing but not timeupdate, then the video is stopped unexpectedly
// set it to ended
const debounceCheckVideoNotStoppedButStatusIsPlaying = debounce(() => {
  if (playerState.value !== PlayerState.Playing) {
    return;
  }

  player.value.pause();
  playerState.value = PlayerState.Ended;
  emit('player-unexpectedly-ended');
}, 10000);

const initPlayer = () => {
  if (props.streamingType == 'live') {
    // Amazon IVS 專用初始化
    player.value = videojs(
      videoElement.value,
      {
        techOrder: ['AmazonIVS'],
      },
      () => {
        player.value.on('error', (error) => {
          emitError(error);
        });

        player.value.on('timeupdate', () => {
          debounceCheckVideoNotStoppedButStatusIsPlaying();
        });

        toRaw(player.value).enableIVSQualityPlugin();
        const playerEvent = player.value.getIVSEvents().PlayerEventType;
        const ivsPlayerState = player.value.getIVSEvents().PlayerState;

        if (IvsPlayerInstance.value) {
          IvsPlayerInstance.value.addEventListener(playerEvent.ERROR, emitError);
          IvsPlayerInstance.value.addEventListener(ivsPlayerState.ENDED, onVideoEnded);
          IvsPlayerInstance.value.addEventListener(ivsPlayerState.PLAYING, onStartPlayer);

          if (props.src) {
            player.value.src(props.src);
          }
        }
        playerState.value = PlayerState.Init;
      },
    );
  } else if (props.streamingType == 'replay') {
    // 標準 VOD / HLS 初始化
    const vodOptions = {
      language: 'en',
      sources: [{ src: props.src, type: 'application/x-mpegURL' }],
      errorDisplay: false,
      loadingSpinner: true,
      controlBar: {
        skipButtons: {
          forward: 10,
          backward: 10,
        },
      },
      html5: {
        vhs: {
          withCredentials: true,
          enableLowInitialPlaylist: false,
          experimentalBufferBasedABR: false,
          handlePartialData: true,
        },
      },
    };

    player.value = videojs(videoElement.value, vodOptions);

    // 使用 ready 確保播放器 DOM 好了 立刻掛載監聽與插件
    player.value.ready(function () {
      const p = this;

      const qualityLevels = p.qualityLevels();
      qualityLevels.on('change', () => {
        const p = player.value;
        if (p.ended()) {
          return;
        }
        const currentTime = p.currentTime();
        // 切換畫質時不更新聊天室
        isQualitySwitching.value = true;
        requestAnimationFrame(() => {
          if (p) {
            p.currentTime(currentTime);
          }
        });
        setTimeout(() => {
          isQualitySwitching.value = false;
        }, 5000);
      });

      p.on('error', (error) => emitError(error));
      p.on('play', onStartPlayer);
      p.on('seeked', onVideoSeeked);
      p.on('seeking', onVideoSeeking);
      p.on('pause', onVideoPaused);
      p.on('waiting', onVideoWaiting);
      p.on('playing', onVideoPlaying);

      // 在 src 加載前啟動畫質插件 (IOS並不支援套件)
      if (!videojs.browser.IS_IOS) {
        p.hlsQualitySelector({
          displayCurrentQuality: true,
        });
      }

      // 手動餵入 src，觸發 HLS 解析
      if (props.src) {
        p.src({
          src: props.src,
          type: 'application/x-mpegURL',
        });
      }

      playerState.value = PlayerState.Init;
    });
  }
};

// 監聽頁面是否可見 同步給回放聊天室
const handleVisibilityChange = () => {
  if (document.visibilityState === 'visible') {
    if (player.value && props.streamingType === 'replay') {
      const currentTimeMs = player.value.currentTime() * 1000;
      emit('video-seeked', currentTimeMs);
    }
  }
};

// 將對播放器時間軸調整的資訊經過父物件傳遞給子物件
// VideoPlayer -> PlayerPage -> StreamingPage -> ChatRoom -> ChatMessage
const onVideoSeeked = () => {
  if (isQualitySwitching.value) {
    isQualitySwitching.value = false;
    return;
  }

  const newTime = player.value.currentTime() * 1000; // 從 video.js 實例獲取當前時間
  isVideoSeeking.value = false;
  emit('video-seeked', newTime);
};
const onVideoSeeking = () => {
  if (isQualitySwitching.value) {
    return;
  }

  if (!isVideoSeeking.value) {
    isVideoSeeking.value = true;
    emit('video-seeking');
  }
};
const onVideoPaused = () => {
  emit('video-paused');
};
const onVideoWaiting = () => {
  isVideoWait.value = true;
  emit('video-paused');
};
const onVideoPlaying = () => {
  if (isVideoWait.value) {
    const newTime = player.value.currentTime() * 1000;
    emit('video-played', newTime);
    isVideoWait.value = false;
  }
};

const emitError = (error) => {
  playerState.value = PlayerState.Error;
  emit('player-error', error);
};

const changeSrc = () => {
  if (player?.value && typeof player.value.src === 'function') {
    if (props.streamingType === 'live') {
      // workaround for reload video
      player.value.src('');
      player.value.src(props.src);
    } else {
      player.value.src({
        src: props.src,
        type: 'application/x-mpegURL',
      });
      player.value.load();
    }
  }
};

const disposePlayer = () => {
  player.value?.dispose();
};

onMounted(() => {
  document.addEventListener('visibilitychange', handleVisibilityChange);
  // 底層攔截器 攔截所有影片請求
  originalXMLHttpRequestOpen = XMLHttpRequest.prototype.open;
  XMLHttpRequest.prototype.open = function (method, url) {
    this.addEventListener('readystatechange', () => {
      if (this.readyState === 4) {
        if (this.status === 403 || this.status === 401) {
          if (url.includes('.m4s') || url.includes('.m3u8')) {
            // 影片因為授權失敗未取得 呼叫方法刷新 cookie
            emit('token-refresh');
          }
        }
      }
    });
    return originalXMLHttpRequestOpen.apply(this, arguments);
  };

  try {
    initPlayer();
  } catch (error) {
    playerState.value = PlayerState.Error;
    emitError(error);
  }
});

// 離開網頁時停止子母畫面
const forceExitPiP = async () => {
  if (document.pictureInPictureElement) {
    try {
      await document.exitPictureInPicture();
    } catch (error) {
      console.error(error);
    }
  }
};

onUnmounted(() => {
  document.removeEventListener('visibilitychange', handleVisibilityChange);
  forceExitPiP();
  try {
    // IVS
    if (props.streamingType == 'live') {
      const playerEvent = player.value.getIVSEvents().PlayerEventType;
      const ivsPlayerState = player.value.getIVSEvents().PlayerState;

      IvsPlayerInstance.value.removeEventListener(playerEvent.ERROR, emitError);

      IvsPlayerInstance.value.removeEventListener(ivsPlayerState.ENDED, onVideoEnded);
      IvsPlayerInstance.value.removeEventListener(ivsPlayerState.PLAYING, onStartPlayer);
    }

    // 還原原始 XMLHttpRequest.open 方法
    if (originalXMLHttpRequestOpen) {
      XMLHttpRequest.prototype.open = originalXMLHttpRequestOpen;
    }

    disposePlayer();
  } catch (error) {
    emitError(error);
  }
});

const onVideoEnded = () => {
  playerState.value = PlayerState.Ended;
  emit('player-ended');
};

const onStartPlayer = () => {
  playerState.value = PlayerState.Playing;
  emit('player-start');
  const newTime = player.value.currentTime() * 1000;
  emit('video-played', newTime);
};

watch(
  () => props.src,
  (newSrc, oldSrc) => {
    if (newSrc !== oldSrc && newSrc !== '') {
      changeSrc();
    }
  },
);

const playVideo = () => {
  if (!player.value) return;

  // 直播 IVS 用
  if (props.streamingType == 'live') {
    // workaround for reload video
    player.value.src('');
    player.value.src(props.src);
  }
  // 回放用
  else {
    player.value.src({
      src: props.src,
      type: 'application/x-mpegURL',
    });
    player.value.play();
  }
};

const wakeUpPlayer = () => {
  if (!player.value) return;
  // 藉由瞬間暫停播放來喚醒回放播放器的緩衝機制
  if (!player.value.paused()) {
    player.value.pause();
    player.value.play();
  }
};

defineExpose({
  playVideo,
  playerState,
  wakeUpPlayer,
});
</script>

<style scoped>
:deep(.video-js .vjs-control-bar) {
  align-items: center;
  max-width: 100vw !important;
}

@media screen and (max-width: 480px) {
  :deep(.vjs-skip-backward-10),
  :deep(.vjs-skip-forward-10) {
    display: none !important;
  }
}

:deep(.vjs-progress-control .vjs-play-progress .vjs-time-tooltip) {
  display: none !important;
}

:deep(.vjs-subs-caps-button),
:deep(.vjs-subtitles-button),
:deep(.vjs-captions-button),
:deep(.vjs-descriptions-button) {
  display: none !important;
}
</style>
